import React, { useEffect, useMemo, useState } from 'react';
import {
  Confirm,
  Dimmer,
  Dropdown,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Modal,
  Progress,
} from 'semantic-ui-react';
import styled from 'styled-components';
import Button from '../../../../components/Button';
import {
  dangerNotification,
  successNotification,
  warningNotification,
} from '../../../../services/notification';
import {
  getBatchRestartStatus,
  previewRestartBatch,
  RestartPreviewResponse,
  RestartStatusResponse,
  restartBatch,
} from '../../../../services/fire-insurance-batches';
import {
  buildRestartCompetenceOptions,
  buildRestartConfirmationMessage,
  canRestartBatch,
  RestartCompetenceValue,
  summarizeRestartStatus,
} from '../utils/batch-restart.util';

export interface RestartBatchModalBatch {
  id: string;
  batchNumber: number;
  status: string;
  itemCount: number;
  isManualProcessing: boolean;
  quotationCode?: string;
  proposalCode?: string | null;
  policyNumber?: string | null;
  estate: {
    name: string;
  };
}

interface RestartBatchModalProps {
  open: boolean;
  batch: RestartBatchModalBatch | null;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalPhase = 'configure' | 'confirm' | 'executing' | 'done';

const ContextBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const ContextItem = styled.span`
  color: #444;
  font-size: 13px;

  strong {
    color: #333;
  }
`;

const IntroText = styled.p`
  color: #444;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px;
`;

const ImplicationsList = styled(List)`
  &.ui.list {
    margin: 0 0 16px;
    color: #444;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const PreviewList = styled(List)`
  &.ui.list {
    margin: 12px 0 0;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
`;

function getStatusLabelColor(
  status: string,
): 'green' | 'orange' | 'red' | 'blue' | 'grey' {
  if (status === 'added') {
    return 'green';
  }
  if (status === 'failed') {
    return 'red';
  }
  if (status === 'creating_quote' || status === 'adding') {
    return 'blue';
  }
  return 'grey';
}

const RestartBatchModal: React.FC<RestartBatchModalProps> = ({
  open,
  batch,
  onClose,
  onSuccess,
}) => {
  const competenceOptions = useMemo(() => buildRestartCompetenceOptions(), []);
  const [phase, setPhase] = useState<ModalPhase>('configure');
  const [competenceValue, setCompetenceValue] = useState<RestartCompetenceValue>(
    competenceOptions.default,
  );
  const [preview, setPreview] = useState<RestartPreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [restartId, setRestartId] = useState<string | null>(null);
  const [restartStatus, setRestartStatus] =
    useState<RestartStatusResponse | null>(null);

  const eligibility = useMemo(
    () =>
      batch
        ? canRestartBatch({
            status: batch.status,
            isManualProcessing: batch.isManualProcessing,
            itemCount: batch.itemCount,
          })
        : { allowed: false },
    [batch],
  );

  const selectedCompetence = competenceOptions.options.find(
    (option) => option.value === competenceValue,
  );

  const resetState = () => {
    setPhase('configure');
    setCompetenceValue(competenceOptions.default);
    setPreview(null);
    setLoadingPreview(false);
    setSubmitting(false);
    setRestartId(null);
    setRestartStatus(null);
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (!batch || !eligibility.allowed) {
      return;
    }

    let cancelled = false;
    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const response = await previewRestartBatch(batch.id);
        if (!cancelled) {
          setPreview(response);
        }
      } catch (error) {
        if (!cancelled) {
          dangerNotification(
            'Erro',
            error?.message || 'Não foi possível carregar o preview do reinício.',
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [open, batch?.id, eligibility.allowed]);

  useEffect(() => {
    if (!open || !restartId || phase !== 'executing') {
      return;
    }

    let cancelled = false;
    let notified = false;

    const poll = async () => {
      try {
        const status = await getBatchRestartStatus(restartId);
        if (cancelled) {
          return;
        }
        setRestartStatus(status);

        if (
          status.status === 'COMPLETED' ||
          status.status === 'COMPLETED_WITH_ERRORS' ||
          status.status === 'FAILED'
        ) {
          setPhase('done');
          if (!notified) {
            notified = true;
            const summary = summarizeRestartStatus(status.items || []);
            if (status.status === 'FAILED') {
              dangerNotification(
                'Reinício falhou',
                status.errorMessage || 'Não foi possível reiniciar o lote.',
              );
            } else if (summary.failed > 0) {
              warningNotification(
                'Reinício parcial',
                `${summary.added} item(ns) reiniciados; ${summary.failed} falharam.`,
              );
            } else {
              successNotification(
                'Lote reiniciado',
                `${summary.added} item(ns) reiniciados com sucesso.`,
              );
            }
            onSuccess();
          }
        }
      } catch (error) {
        if (!cancelled) {
          dangerNotification(
            'Erro',
            error?.message || 'Falha ao consultar status do reinício.',
          );
        }
      }
    };

    poll();
    const intervalId = window.setInterval(poll, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [open, restartId, phase, onSuccess]);

  const handleClose = () => {
    if (submitting || phase === 'executing') {
      return;
    }
    onClose();
  };

  const handleRequestConfirm = () => {
    if (!eligibility.allowed || !selectedCompetence) {
      return;
    }
    setPhase('confirm');
  };

  const handleConfirm = async () => {
    if (!batch || !selectedCompetence) {
      return;
    }

    setSubmitting(true);
    setPhase('executing');
    try {
      const response = await restartBatch(batch.id, {
        competenceMonth: selectedCompetence.startDate,
      });
      setRestartId(response.restartId);
    } catch (error) {
      setPhase('configure');
      dangerNotification(
        'Erro',
        error?.message || 'Não foi possível iniciar o reinício do lote.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const itemCount = preview?.itemCount ?? batch?.itemCount ?? 0;
  const statusSummary = summarizeRestartStatus(restartStatus?.items || []);
  const progressPercent =
    statusSummary.total === 0
      ? 0
      : Math.round(
          ((statusSummary.added + statusSummary.failed) / statusSummary.total) *
            100,
        );

  if (!batch) {
    return null;
  }

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeOnDimmerClick={!submitting && phase !== 'executing'}
      >
        <Modal.Header>
          <Icon name="refresh" />
          Reiniciar lote
        </Modal.Header>
        <Modal.Content scrolling>
          <Dimmer active={loadingPreview} inverted>
            <Loader>Carregando itens do lote...</Loader>
          </Dimmer>

          <ContextBanner>
            <ContextItem>
              <strong>Lote:</strong> #{batch.batchNumber}
            </ContextItem>
            <ContextItem>
              <strong>Imobiliária:</strong> {batch.estate.name}
            </ContextItem>
            <ContextItem>
              <strong>Itens ativos:</strong> {itemCount}
            </ContextItem>
            <ContextItem>
              <strong>Cotação atual:</strong>{' '}
              {preview?.oldQuotationCode || batch.quotationCode || '-'}
            </ContextItem>
          </ContextBanner>

          {phase === 'configure' && (
            <>
              <IntroText>
                Esta ação cria uma <strong>nova cotação/apólice mãe</strong> na
                Yelum e readiciona os itens ativos do lote nessa nova cotação.
              </IntroText>

              <ImplicationsList bulleted>
                <List.Item>
                  Os itens serão adicionados via <strong>add item</strong>.
                </List.Item>
                <List.Item>
                  Certificados de apólice atuais serão removidos (serão gerados
                  no próximo fechamento).
                </List.Item>
                <List.Item>
                  Todos os itens voltam para status{' '}
                  <strong>Apólice a faturar</strong>.
                </List.Item>
                <List.Item>
                  Números de proposta, cotação e apólice serão resetados.
                </List.Item>
                <List.Item>
                  A transmissão deste mês será considerada efetuada (nova data de
                  transmissão).
                </List.Item>
              </ImplicationsList>

              <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                Mês de competência (vigência)
              </label>
              <Dropdown
                fluid
                selection
                options={competenceOptions.options.map((option) => ({
                  key: option.value,
                  value: option.value,
                  text: option.label,
                }))}
                value={competenceValue}
                onChange={(_, data) =>
                  setCompetenceValue(data.value as RestartCompetenceValue)
                }
                disabled={submitting || loadingPreview}
              />

              {!eligibility.allowed && (
                <Message warning style={{ marginTop: 12 }}>
                  {eligibility.reason}
                </Message>
              )}
            </>
          )}

          {(phase === 'executing' || phase === 'done') && (
            <>
              <p style={{ marginBottom: 12 }}>
                {phase === 'executing'
                  ? 'Processando reinício do lote...'
                  : 'Resultado do reinício:'}
              </p>
              <Progress
                percent={progressPercent}
                indicating={phase === 'executing'}
                success={phase === 'done' && statusSummary.failed === 0}
                warning={phase === 'done' && statusSummary.failed > 0}
                progress
              />

              {restartStatus?.newQuotationCode && (
                <Message info size="small">
                  Nova cotação: <strong>{restartStatus.newQuotationCode}</strong>
                </Message>
              )}

              <PreviewList divided relaxed>
                {(restartStatus?.items ?? []).map((item) => (
                  <List.Item key={item.policyId}>
                    <Label color={getStatusLabelColor(item.status)}>
                      {item.tenantName}
                    </Label>{' '}
                    {item.status}
                    {item.newItemNo ? ` — item ${item.newItemNo}` : ''}
                    {item.error ? ` — ${item.error}` : ''}
                  </List.Item>
                ))}
              </PreviewList>

              {phase === 'done' && (
                <Message
                  positive={statusSummary.failed === 0 && restartStatus?.status !== 'FAILED'}
                  warning={statusSummary.failed > 0}
                  negative={restartStatus?.status === 'FAILED'}
                  style={{ marginTop: 12 }}
                >
                  {restartStatus?.status === 'FAILED'
                    ? restartStatus.errorMessage || 'Reinício falhou.'
                    : `${statusSummary.added} de ${statusSummary.total} item(ns) reiniciados com sucesso.`}
                </Message>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <ActionsRow>
            <Button
              basic
              onClick={handleClose}
              disabled={submitting || phase === 'executing'}
            >
              {phase === 'done' ? 'Fechar' : 'Cancelar'}
            </Button>
            {phase === 'configure' && (
              <Button
                color="red"
                loading={submitting}
                disabled={!eligibility.allowed || loadingPreview || itemCount === 0}
                onClick={handleRequestConfirm}
              >
                Confirmar reinício
              </Button>
            )}
          </ActionsRow>
        </Modal.Actions>
      </Modal>

      <Confirm
        open={phase === 'confirm'}
        header="Confirmar reinício do lote"
        content={buildRestartConfirmationMessage(
          itemCount,
          selectedCompetence?.label || '',
        )}
        cancelButton="Voltar"
        confirmButton="Reiniciar lote"
        onCancel={() => setPhase('configure')}
        onConfirm={handleConfirm}
      />
    </>
  );
};

export default RestartBatchModal;
